import React, {createRef, memo, useContext, useEffect, useState} from 'react';
import { useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import moment from "moment";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import {Icon, useTheme} from "@material-ui/core";
import {FreighterUnloading} from "../../../icons";
import {
  defaultComponentsScroll,
  FullWidthFilter,
  MaterialTableBody
} from '../../../controls/Material-Table-Components';
import SystemContext from "../../../SystemContext";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import {Loading} from "../../../controls";
import RefreshCheck from '../../../controls/RouteView2/RefreshCheck';
import {ColumnWidth, dowNumberSearch} from "../../../blocks/ColumnHelper";
import useDimensions from "../../../blocks/useDimensions";
import { useOutstandingSlrs} from "../../../services/booking";
import EditFlightCargoDialog from "./EditFlightCargoDialog";
import EditMapDialog from "./EditMapDialog";
import ViewBooking from "../../Booking/components/ViewBooking";
import Dialog from "../../../controls/Dialog";
import BuildTruckRouteIconFromLegs from "../../Trips/utils/BuildTruckRouteIconFromLegs";

const apiSitePrefix = getApiSitePrefix();

const formatChars={
  "U": "[A-Za-z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "n": "-",
  "x": "."
}

const flightNumberSearch = (fieldName) => {
  return  (term, rowData) => {
    if (!__.has(rowData, fieldName)) return __.isEmpty(term);
    const airlineCode = rowData.airlineCode;
    const flightNo = rowData.flightNo;
    const flightNumber = airlineCode + flightNo;
    if (airlineCode && airlineCode.startsWith(term.toUpperCase()))
      return true;
    if (flightNo && flightNo.startsWith(term.toUpperCase()))
      return true;
    if (flightNumber && flightNumber.startsWith(term.toUpperCase()))
      return true;
    return flightNumber === term.toUpperCase();
  }
}

const tripSearch = (fieldname) => {
  return  (term, rowData) => {
    const termUpper = `${term}`.replace("-","").toUpperCase();
    if (!__.has(rowData, fieldname)) return __.isEmpty(term);

    const tripPrefix =  rowData["tripPrefix"];
    const tripNo = rowData["tripNo"];

    if (`${tripPrefix}${tripNo}`.replace("-","").startsWith(termUpper)) return true;
    if (`${tripNo}`.startsWith(termUpper)) return true;

    return false;
  }
}


const FlightListOutstandingSlr = (_props) => {
  const {isAuthenticated, user} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;

  const props = {
    ..._props
  }
  const classes = props.classes;
  const colours = SystemData.colours;

  const operatorName = __.startCase(__.toLower(process.env.REACT_APP_COMPANY)) || "Magma";

  const tableRef = createRef({state: {lastEditingRow: false}});
  const month = props.month;
  const year = props.year;
  const company = props.company
  const userClass = props.userClass;

  const queryClient = useQueryClient();
  const extraKeys = [];

  const flights = useOutstandingSlrs({tableRef, queryClient, extraKeys});
  const [state] = useState({
    canEdit: props.canEdit,
    canEditFuelLogs: props.canEditFuelLogs,
    year: props.year,
    month: props.month
  });

  const [defaultValues, setDefaultValues] = useState(null);


  useEffect(()=> {
    if (eventBus) {
      eventBus.bind('FlightList.TripAction.RefreshList', refreshList);
      return ()=>{
        eventBus.unbind('FlightList.TripAction.RefreshList', refreshList);
      }
    }
  }, [eventBus]);

  let selectedRow = -1;

  const getDefaultValues = async () => {
    try {
      const response = await axios.get(`${apiSitePrefix}/tripsheet/defaults`);
      if (response.data && response.data.success) {
        setDefaultValues(response.data.defaultValues);
        return response.data.defaultValues;
      }

      if (!response.data) {
        console.log(`Could not retrieve Trip sheet Defaults. Server is not responding`);
        SystemData.alert("Error", `Could not retrieve Trips sheet Defaults. Server is not responding`);
      } else if (!response.data.success) {
        console.log(`Could not retrieve Trips' Defaults. Server returned ${response.data.error}`);
        SystemData.alert("Error", `Could not retrieve Trips sheet Defaults. Server returned ${response.data.error}`);
      }
    } catch (err) {
      console.log(`Could not retrieve Trips sheet Defaults. Server is not responding`);
      SystemData.alert("Error", `Could not retrieve Trips sheet Defaults. Server is not responding`);
    }
  }

  const refreshList = async () => {
    queryClient.invalidateQueries(["Outstanding SLR"]);
  }

  async function downloadFlightSchedule(target, filter, download = true, companyUuid = null, companyName = "") {

    if (!target)
      return;

    const startDateMoment = moment(`${props.year}-${props.month}-01`);
    const startDate = startDateMoment.isValid() ? startDateMoment.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
    const endDate = moment(startDate).endOf("month").format("YYYY-MM-DD");
    const route = target === "supplier" ? "supplierschedule" : target === "hoursFuelReport" ? "hoursFuelReport" : target === "airline" ? "lessorschedule" : target==="customer"?"customerschedule":"airportschedule";

    let url = `${apiSitePrefix}/trip/${route}/${startDate}/${endDate}/${filter}`;

    if (url) {
      try {
        SystemData.toggleLoadingProgress(true, `Preparing Flights Report ...`);
        const response = await axios.get(url, {
          headers: {
            //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
            'Access-Control-Allow-Origin': '*',
          }
        });

        if (response && response.data) {
          SystemData.toggleLoadingProgress(false);
          if (response.data.status) {
            if (!download) {
              let templateType;
              let subject;
              let attachmentDescription;
              const displayDateFormat = "DD MMM YYYY";
              const scheduleFrom = startDateMoment.isValid() ? startDateMoment.format(displayDateFormat) : moment().format(displayDateFormat);
              const scheduleTo = moment(scheduleFrom, displayDateFormat).endOf("month").format(displayDateFormat);
              const fileName = response.data.downloadFileName;
              switch (target) {
                case "airline":
                  subject = `${operatorName} Flight Schedule // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "OPERATOR_FLIGHTS";
                  attachmentDescription = `${operatorName} flight schedule effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
                case "customer":
                  subject = `${operatorName}  Flight Schedule // ${companyName} // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "CUSTOMER_FLIGHTS";
                  attachmentDescription = `${operatorName} flight schedule for ${companyName} effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
                default:
                  subject = `${operatorName} Flight Schedule // ${filter} // ${scheduleFrom} to ${scheduleTo}`;
                  templateType = "AIRPORT_FLIGHTS";
                  attachmentDescription = `${operatorName} flight schedule via ${filter} effective from ${scheduleFrom} to ${scheduleTo}`
                  break;
              }

              const emailRecord = {
                target: target,
                targetFilter: target === "airport" && companyUuid ? companyUuid : filter,
                recipients: "",
                carbonCopy: "",
                subject: subject,
                templateType: templateType,
                templateUuid: "dummyTemplateUuid",
                attachmentDescription: attachmentDescription,
                attachmentUrl: response.data.excelSignedUrl,
                fileName: fileName.replaceAll("/",""),
                repeatData: [],
                contactRole: "FLIGHTS",
                airportCode: target === "airport"?filter:undefined
              }
              SystemData.sendEmail(emailRecord, `Send Flight Schedule`, true);

              return;
            }

            const fileName = response.data.downloadFileName;
            let a = document.createElement('a');
            a.href = response.data.excelSignedUrl;
            a.download = fileName;
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { document.body.removeChild(a); }, 0);
          } else {
            SystemData.openAlert(
              "Download Error",
              `The report could not be downloaded due to the following error: ${response.data.error}`
            );
          }
        }
      } catch (err) {
        SystemData.toggleLoadingProgress(false);
        SystemData.openAlert(
          "Download Error",
          `The Schedule report could not be downloaded due to the following unexpected error: ${err}`
        );
      }
    }
  }


  const handleRowClick = (event, rowData) => {
    if (rowData.tableData.id >= 0) {
      selectedRow = rowData.tableData.id;
    }
  }

  const getFlightColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize: 12, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      filterCellStyle: { fontSize: "0.7rem", padding: "4px 2px" },
      editable: "never",
      sorting: false,
      resizable: false,
      emptyValue: "",
      initialEditValue: "",
    };

    const cellStyle = { fontSize: 12, padding: "4px 2px" };
    const borderedCellStyle = { fontSize: 11, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #f5f5f5" };
    const invisibleBorderedCellStyle = { fontSize: 11, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #ffffff" };
    const boldInvisibleBorderedCellStyle = { fontSize: 13, fontWeight: 800, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #ffffff" };
    const borderedHeaderStyle = { borderLeft: "0px solid rgb(63, 63, 63)", borderRight: "1px solid rgb(63, 63, 63)" };
    const headerStyle = { fontSize: 12, padding: "4px 6px" };
    const filterStyle = { fontSize: 12, padding: "9px 4px", marginLeft: -12 };
    // const borderedFilterStyle = {}; //{ borderLeft: "0px solid #ffffff", borderRight: "1px solid #ffffff" };

    return [
      //{...validatorColumn},
      {
        ...baseColumn,
        ...ColumnWidth(70, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "Flight",
        field: "flightNo",
        editable: "never",
        filterComponent: FullWidthFilter,
        render: rowData => {
          return ( <span>{`${rowData.transportType === "TRUCK" ? `Truck` : `${rowData.airlineCode}-${rowData.flightNo}`}`}</span>);
        },
        customFilterAndSearch: flightNumberSearch('flightNo')
      },
      {
        ...baseColumn,
        ...ColumnWidth(50, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "Day",
        field: "flightDate",
        render: rowData => {
          const flightDate = moment(rowData.flightDate, "YYYY-MM-DD HH:mm:ss");
          return ( <div style={{textAlign: "center"}}>{flightDate.isoWeekday()}</div>);
        },
        filterComponent: FullWidthFilter,
        customFilterAndSearch: dowNumberSearch('flightDate')
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "Date",
        field: "flightDate",
        render: rowData => {
          const flightDate = moment(rowData.flightDate, "YYYY-MM-DD HH:mm:ss");
          return ( <span>{flightDate.format("DD MMM")}</span>);
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "STD",
        field: "scheduledDepartureTimeUTC",
        render: rowData => {
          return ( <span>{ moment.utc(rowData.scheduledDepartureTimeUTC, "YYYY-MM-DD HH:mm:ss").format("HH:mm")}</span>);
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "ETD",
        field: "estimatedDepartureTimeUTC",
        render: rowData => {
          return ( <span>{ moment(rowData.scheduledDepartureTimeUTC, "YYYY-MM-DD HH:mm:ss").format("HH:mm")}</span>);
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "ADEP",
        field: "originAirportCode",
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "ADES",
        field: "destinationAirportCode",
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "STA",
        field: "scheduledArrivalTimeUTC",
        render: rowData => {
          return ( <span>{ moment(rowData.scheduledArrivalTimeUTC, "YYYY-MM-DD HH:mm:ss").format("HH:mm")}</span>);
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "ETA",
        field: "estimatedArrivalTimeUTC",
        render: rowData => {
          return ( <span>{ moment(rowData.scheduledArrivalTimeUTC, "YYYY-MM-DD HH:mm:ss").format("HH:mm")}</span>);
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "ALTN",
        field: "alternateAirportCode",
        render: rowData => {
          return ( <span>{ `${rowData.transportType === "TRUCK" ? `` : `(${rowData.alternateAirportCode})`}`}</span>);
        },
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "ACFT",
        field: "aircraftReg",
        filterComponent: FullWidthFilter,
        render: rowData => {
          if (rowData.transportType === "TRUCK") {
            return (
              <div>
                <BuildTruckRouteIconFromLegs legs={[rowData]} style={{paddingRight: 5, paddingBottom: 2}}/>
              </div>
            )
          }
          return ( <span>{ `${rowData.transportType === "TRUCK" ? `` : `${rowData.aircraftReg}`}`}</span>);
        },
      },
      {
        ...baseColumn,
        ...ColumnWidth(95, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "Trip",
        field: "tripPrefix",
        render: rowData => {
          return ( <span>{ `${rowData.tripPrefix}-${rowData.tripNo}`}</span>);
        },
        filterComponent: FullWidthFilter,
        customFilterAndSearch: tripSearch('tripPrefix')
      },
      {
        ...baseColumn,
        ...ColumnWidth(115, 4, undefined, cellStyle, headerStyle, filterStyle, false),
        title: "SLR Reference",
        field: "slrReference",
        filterComponent: FullWidthFilter,
        // customFilterAndSearch: tripSearch('tripPrefix')
      },
      {
        ...baseColumn,
        ...ColumnWidth(100, 2, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "Customer",
        field: "slrCompanyName",
        filtering: false,
      },
      {
        ...baseColumn,
        ...ColumnWidth(150, 2, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "Special load",
        field: "specialLoadName",
        filtering: false,
      },
      {
        ...baseColumn,
        ...ColumnWidth(200, 2, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "Cargo Description",
        field: "cargoDescription",
        filtering: false,
      },
      {
        ...baseColumn,
        ...ColumnWidth(200, 2, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "Special Info",
        field: "specialInfo",
        filtering: false,
      },
      {
        ...baseColumn,
        ...ColumnWidth(200, 2, undefined, borderedCellStyle, headerStyle, undefined, false),
        title: "Loading Notes",
        field: "loadabilityNotes",
        filtering: false,
      },
      {
        ...baseColumn,
        title: "",
        filtering: false,
      },
    ];
  };

  const FlightRows = memo((props) => {
    const theader = tableContainerRef.current?.querySelector('thead');
    const thead = theader ? theader.clientHeight:30;
    const bodyHeight = tableHeight - thead - 40;

    return (
      <MaterialTable
        icons={{ Filter: React.forwardRef((props, ref) => <Icon className="filterIcon">filter_list</Icon> )}}
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        onRowClick={handleRowClick}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "4px 2px",
          },
          filterCellStyle: {
            padding: "4px 2px",
            fontSize: "0.7rem",
          },
          headerRowStyle: {
            display: "table",
            width: "100%",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "4px 2px",
          },
          filterRowStyle: {
            paddingLeft: state.canEdit ? 0 : 0,
            width: state.canEdit ? "calc(100% - 20px)" : "100%",
          },
          highlightRowStyle: {backgroundColor: "#F69259"},
          rowStyle: (rowData)=> ({opacity: rowData.cancelled?0.5:1, borderBottom: `${rowData.dateChange?"3":"1"}px solid rgba(224, 224, 224, 1)`}),
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          paging: false,
          filtering: true,
          search: false,
          title: "Flight List"
        }}
        components={{
          ...defaultComponentsScroll,
          Body: (props) => (
            <MaterialTableBody
              {...props}
              rowHeight={46}
              overscanRowCount={20}
              addVirtualScrolling={true}
              bodyHeight={bodyHeight}
              headerHeight={thead}
              filterRowHeight={46}
            />
          )
        }}
        localization={{
          header: {
            actions: `Actions${String.fromCharCode(160)}`
          },
        }}
        actions={[
          (rowData) => (
            {
              hidden: false,
              icon: () => (
                <FreighterUnloading style={{width: 24, height: 24, color: (rowData.tableData.id === selectedRow ? '#ffffff' : theme.palette.primary.main) }}/>
              ),
              iconProps: { style: {color: (rowData.tableData.id === selectedRow ? '#ffffff' : theme.palette.primary.main), marginLeft: 30}},
              tooltip: 'View Special Load Request',
              isFreeAction: false,
              onClick: (e, rowData) => {
                eventBus.trigger("ViewBooking.open",{open: true, bookingUuid: rowData.bookingUuid});
              }
            }
          ),
          {
            icon: 'refresh',
            tooltip: "Refresh List",
            isFreeAction: true,
            onClick: (e) => {
              refreshList();
            }
          },
        ]}
        title={`Outstanding Special Loads`}
        columns={getFlightColumns({canEdit: state.canEdit})}
        data={props.data}
      />
    )
  });

  const [tableContainerRef, { width: tableWidth, height: tableHeight}] = useDimensions({});
  if (!defaultValues || __.isEmpty(defaultValues))
    getDefaultValues();

  return (isAuthenticated &&
    <RefreshCheck
      year={props.year}
      month={props.month}
      refreshEventName="FlightList.TripAction.RefreshList"
      checkType="Flight List"
    >
      <div ref={tableContainerRef} className={state.canEdit?classes.FlightListEdit:classes.FlightList} style={{width: "100%", minWidth: "100%", marginLeft: 20, marginRight: 20, noWidth: "calc(100% - 24px)"}}>

        {flights.data
          ?
            <FlightRows data={flights.data}/>
          :
            <Dialog
              message="Loading Flight Data"
              open={true}
              buttons={{}}
            >
              <div style={{display: "flex", justifyContent: "center"}}>
                <Loading/>
              </div>
            </Dialog>
        }
        <EditMapDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          formatChars={formatChars}
          classes={classes}
          canEdit={false}
        />
        <EditFlightCargoDialog
          systemData={SystemData}
          apiSitePrefix={apiSitePrefix}
          extraKeys={extraKeys}
          // handleUpdateFlight={handleUpdateFlight}
          formatChars={formatChars}
          classes={classes}
          canEdit={state.canEdit}
        />
      </div>
      <ViewBooking />
    </RefreshCheck>
  );

}

export default FlightListOutstandingSlr;
